import React, { useEffect, useMemo, useState } from "react";
import { Button, Stack, Typography, TextField } from "@mui/material";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { UserCirclePlus } from "@phosphor-icons/react";
import * as selectors from "selectors";
import ProviderListItem from "./ProviderListItem";
import ProviderFormDialog, { ProviderFormDialogType } from "./ProviderFormDialog";
import { useGlobalToast } from "components/GlobalToastProvider";
import useGetCustomerPhysicians from "hooks/api/client/useGetCustomerPhysicians";
import useGetAllDocumentRequests from "hooks/api/client/useGetAllDocumentRequests";
import useUpdateFrequencyCareCoordination from "hooks/api/client/useUpdateFrequencyCareCoordination";
import SectionWrapper from "../../Widgets/SectionWrapper";

/**
 * Providers component that manages its own state and data fetching.
 * This component handles external physician providers independently with its own edit mode.
 * Extracts clientId from URL parameters - no props required.
 */
interface ProvidersProps {
  useSectionWrapper?: boolean;
}

const Providers: React.FC<ProvidersProps> = ({ useSectionWrapper = false }) => {
  const { id: clientId } = useParams<{ id: string }>();
  const { showToast } = useGlobalToast();

  // Fetch data using React Query hooks
  const { physicians: providers, refetchPhysicians } = useGetCustomerPhysicians(clientId);
  const { documentRequests, refetchDocumentRequests } = useGetAllDocumentRequests(clientId);
  const { updateFrequencyAsync } = useUpdateFrequencyCareCoordination();

  const isClinician = useSelector(selectors.isClinician);
  const userPermissions: any = useSelector(selectors.getUserPermissionsList);

  const canEdit = isClinician || userPermissions?.download_phi_release_request;

  const [editMode, setEditMode] = useState(false);
  const [frequencyOfCareCoordination, setFrequencyOfCareCoordination] = useState("");
  const [providerFormType, setProviderFormType] = useState<string | null>(null);
  const [providerFormValues, setProviderFormValues] = useState<any>(null);

  // Sync frequency from first provider's API response (client-level field)
  const providerFrequencyFromApi = useMemo(() => {
    return providers.find((p) => p?.frequency_care_coordination)?.frequency_care_coordination || "";
  }, [providers]);

  // Load frequency from API on mount, refresh when exiting edit mode
  useEffect(() => {
    if (!editMode) {
      setFrequencyOfCareCoordination(providerFrequencyFromApi);
    }
  }, [providerFrequencyFromApi, editMode]);

  const handleAddProvider = (): void => {
    setProviderFormType(ProviderFormDialogType.newProvider);
  };

  const handleEditProvider = (provider: any): void => {
    setProviderFormType(ProviderFormDialogType.updateProvider);
    setProviderFormValues({
      id: provider.id,
      type: provider.physician_type,
      isReferringProvider: provider.is_referring_provider || false,
    });
  };

  const handleCreateROI = (provider: any): void => {
    setProviderFormValues({ id: provider.id, type: provider.physician_type });
    setProviderFormType(ProviderFormDialogType.roi);
  };

  const handleFormClose = (): void => {
    setProviderFormType(null);
    setProviderFormValues(null);
  };

  const handleProviderChanges = (ROICreated?: boolean): void => {
    const isEdit = providerFormType === ProviderFormDialogType.updateProvider;
    const isNew = providerFormType === ProviderFormDialogType.newProvider;

    let actionType = "removed";
    if (isNew) actionType = "added";
    else if (isEdit) actionType = "updated";

    let message = `Provider successfully ${actionType}`;

    if (ROICreated) {
      message = "Release of Information successfully created";
    }

    // Refresh providers and document requests data
    refetchPhysicians();
    refetchDocumentRequests();

    showToast({ message });
    setProviderFormType(null);
    setProviderFormValues(null);
  };

  if (!clientId) {
    return (
      <Typography variant="h6" color="error">
        Client ID is required
      </Typography>
    );
  }

  const hasProviders = providers.length > 0;

  const handleCancel = () => {
    // Restore original frequency value and exit edit mode
    setFrequencyOfCareCoordination(providerFrequencyFromApi);
    setEditMode(false);
  };

  const handleSave = async () => {
    // Persist frequency when saving (client-level field)
    if (frequencyOfCareCoordination !== providerFrequencyFromApi) {
      // Update frequency using custom hook (handles error/success toasts)
      try {
        await updateFrequencyAsync({
          clientId,
          frequency: frequencyOfCareCoordination || null,
        });
      } catch {
        // Error handling is done by the hook
        return;
      }
    }
    setEditMode(false);
  };

  const handleEditClick = () => {
    setEditMode(true);
  };

  const content = (
    <Stack gap="24px">
      {/* Header Section */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack gap="8px" flex={1}>
          <Typography variant="h3" sx={{ fontSize: "20px", fontWeight: 500, lineHeight: 1.6 }}>
            External Providers
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: 1.43,
              color: "text.secondary",
            }}
          >
            External physician providers linked with the client&apos;s account.
          </Typography>
        </Stack>
        {canEdit && !editMode && (
          <Button variant="contained" size="small" color="secondary" onClick={handleEditClick}>
            Edit
          </Button>
        )}
        {canEdit && editMode && (
          <Stack direction="row" gap="8px">
            <Button variant="contained" size="small" color="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" size="small" color="primary" onClick={handleSave}>
              Save Changes
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Frequency Field */}
      <Stack gap="8px">
        {editMode ? (
          <TextField
            fullWidth
            label="Frequency of Care Coordination Anticipated"
            value={frequencyOfCareCoordination}
            onChange={(e) => setFrequencyOfCareCoordination(e.target.value)}
            placeholder="Enter frequency"
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        ) : (
          <>
            <Typography
              variant="caption"
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                lineHeight: 1,
                color: "text.secondary",
                letterSpacing: "0.3px",
              }}
            >
              Frequency of Care Coordination Anticipated
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                lineHeight: 1.43,
                color: "text.primary",
              }}
            >
              {frequencyOfCareCoordination || "-"}
            </Typography>
          </>
        )}
      </Stack>

      {/* Providers List */}
      <Stack gap="8px">
        {providers.map((provider) => (
          <ProviderListItem
            key={provider.id}
            provider={provider}
            canEdit={canEdit}
            editMode={editMode}
            onEdit={() => handleEditProvider(provider)}
            onCreateROI={() => handleCreateROI(provider)}
            onProviderChanges={handleProviderChanges}
            documentRequests={documentRequests}
            userPermissions={userPermissions}
          />
        ))}
        {!hasProviders && (
          <Typography
            component="p"
            variant="body2"
            color="textSecondary"
            sx={{ width: "100%", lineHeight: 6 }}
          >
            There is no physician information
          </Typography>
        )}
      </Stack>

      {/* Add Provider Button (Edit Mode Only) */}
      {canEdit && editMode && (
        <Button
          color="secondary"
          onClick={handleAddProvider}
          startIcon={<UserCirclePlus size={24} />}
          sx={{ width: "fit-content" }}
        >
          Add Provider
        </Button>
      )}
    </Stack>
  );

  return (
    <>
      {useSectionWrapper ? (
        <SectionWrapper headingBottomSpacing={0} sectionPadding={0} hideButtons>
          {content}
        </SectionWrapper>
      ) : (
        content
      )}

      {providerFormType && (
        <ProviderFormDialog
          open={Boolean(providerFormType)}
          closeDialog={handleFormClose}
          clientId={clientId}
          frequencyOfCareCoordination={frequencyOfCareCoordination}
          formDialogType={providerFormType}
          providerFormValues={providerFormValues}
          onProviderChanges={handleProviderChanges}
          userPermissions={userPermissions}
        />
      )}
    </>
  );
};

export default Providers;
